import { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { handleSimpleFormNav } from "@/lib/keyboardNav";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Layers } from "lucide-react";
import { toast } from "sonner";
import ImportExportBar from "@/components/ImportExportBar";
import { PaginationBar, usePagination } from "@/components/Pagination";

const empty = { group_code: "", name: "", description: "", sector: "" };

export default function GroupMaster() {
  const [list, setList] = useState([]);
  const pg = usePagination(list);
  const [sectors, setSectors] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [f, setF] = useState(empty);

  const load = () => api.get("/groups").then(r => setList(r.data));
  useEffect(() => {
    load();
    api.get("/sectors").then(r => setSectors(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    try {
      const payload = { group_code: f.group_code || null, name: f.name, description: f.description || null, sector: f.sector || null };
      if (editId) await api.patch(`/groups/${editId}`, payload);
      else await api.post("/groups", payload);
      toast.success(editId ? "Group updated" : "Group added");
      setF(empty); setEditId(null); setOpen(false); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const edit = (g) => {
    setF({ group_code: g.group_code || "", name: g.name, description: g.description || "", sector: g.sector || "" });
    setEditId(g.id); setOpen(true);
  };

  const del = async (g) => {
    if (!confirm(`Delete group ${g.group_code || ""} — ${g.name}? This cannot be undone.`)) return;
    try { await api.delete(`/groups/${g.id}`); toast.success("Group deleted"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="p-8">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2"><Layers className="w-3 h-3"/> Inventory · Masters</div>
          <h1 className="font-display font-black text-4xl uppercase tracking-tighter">Group Master</h1>
        </div>
        <div className="flex items-center gap-2">
          <ImportExportBar masterKey="groups" filters={{}} onImported={load} />
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setF(empty); setEditId(null); } }}>
            <DialogTrigger asChild>
              <Button data-testid="add-group-btn" className="rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] font-bold text-xs">
                <Plus className="w-4 h-4 mr-1"/> New Group
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm bg-card border-border max-w-lg">
              <DialogHeader><DialogTitle className="font-display uppercase">{editId ? "Edit" : "New"} Group</DialogTitle></DialogHeader>
              <div className="space-y-3" onKeyDownCapture={handleSimpleFormNav}>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Group Code (blank = auto-generated)</Label>
                  <Input data-testid="group-code" value={f.group_code} onChange={(e) => setF(x => ({ ...x, group_code: e.target.value.toUpperCase() }))} className="mt-1 rounded-sm bg-secondary border-border font-mono uppercase" placeholder="e.g. GRP-ENGINE" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Group Name *</Label>
                  <Input data-testid="group-name" value={f.name} onChange={(e) => setF(x => ({ ...x, name: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border" placeholder="e.g. Engine Parts" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Description</Label>
                  <Input data-testid="group-desc" value={f.description} onChange={(e) => setF(x => ({ ...x, description: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Sector</Label>
                  <Select value={f.sector} onValueChange={(v) => setF({ ...f, sector: v })}>
                    <SelectTrigger data-testid="group-sector" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue placeholder="Select sector"/></SelectTrigger>
                    <SelectContent>{sectors.filter(s => s.status !== "Inactive").map(s => <SelectItem key={s.id} value={s.name}>{s.sector_code ? `${s.sector_code} · ${s.name}` : s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <Button data-testid="save-group-btn" onClick={save} disabled={!f.name} className="w-full rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em]">
                  {editId ? "Update" : "Save"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="border border-border bg-card rounded-sm overflow-hidden overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <tr>
              <th className="text-left p-3">Group Code</th>
              <th className="text-left p-3">Group Name</th>
              <th className="text-left p-3">Sector</th>
              <th className="text-left p-3">Description</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-muted-foreground">No groups yet</td></tr>}
            {pg.pageItems.map(g => (
              <tr key={g.id} data-testid={`group-row-${g.id}`} className="border-t border-border hover:bg-secondary/50">
                <td className="p-3 font-mono text-xs text-primary">{g.group_code || "-"}</td>
                <td className="p-3">{g.name}</td>
                <td className="p-3 text-xs text-muted-foreground">{g.sector || "-"}</td>
                <td className="p-3 text-xs text-muted-foreground">{g.description || "-"}</td>
                <td className="p-3 text-right space-x-1 whitespace-nowrap">
                  <Button data-testid={`edit-group-${g.id}`} size="sm" variant="ghost" onClick={() => edit(g)} className="h-7 w-7 p-0"><Pencil className="w-3 h-3"/></Button>
                  <Button data-testid={`del-group-${g.id}`} size="sm" variant="ghost" onClick={() => del(g)} className="h-7 w-7 p-0 text-destructive"><Trash2 className="w-3 h-3"/></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <PaginationBar pg={pg}/>
      </div>
    </div>
  );
}
